import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';

import { GeoPoint } from '@angular/fire/firestore';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { GroupType } from '../../../models/group.model';
import { UserModel } from '../../../models/user.model';
import * as groupActions from '../../../state/actions/group.actions';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnSubmitComponent } from '../../components/buttons/btn-submit/btn-submit.component';
import { ContactComponent } from '../../components/dropdowns/contact/contact.component';
import { CountryComponent } from '../../components/dropdowns/country/country.component';
import { AddLocationComponent } from '../../components/maps/add-location/add-location.component';
@Component({
  selector: 'app-create-group',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    UppyAngularDashboardModule,
    UppyAngularDashboardModalModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
    ContactComponent,
    CountryComponent,
    BtnSubmitComponent,
    BtnBackComponent,
    AddLocationComponent,
  ],
  templateUrl: './create-group.component.html',
  styleUrls: ['./create-group.component.css'],
})
export class CreateGroupComponent implements OnInit, OnDestroy {
  pageForm: FormGroup;

  uuid = uuidv4();

  user: UserModel | null = null;

  progress: number = 0;
  message: string = '';

  working: boolean = false;

  groupTypeEnum = GroupType;

  groupType = [GroupType.church, GroupType.school];

  exit$ = new Subject<void>();

  options: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
    zoomControl: true,
    center: { lat: 25.2697, lng: 55.3095 },
    scrollwheel: true,
    disableDoubleClickZoom: false,
    zoom: 10,
    fullscreenControl: false,
    streetViewControl: false,
  };

  constructor(private store: Store<AppState>, private toastr: ToastrService) {
    // this.pageForm = new FormGroup({
    //   groupType: new FormControl(GroupType.school),
    //   name: new FormControl('AtChurch Sunday School', [
    //     Validators.required,
    //     Validators.minLength(10),
    //     Validators.maxLength(50),
    //   ]),
    //   description: new FormControl(
    //     "Atchurch's Sunday school for online Biblical studies.",
    //     [
    //       Validators.required,
    //       Validators.minLength(10),
    //       Validators.maxLength(500),
    //     ]
    //   ),
    //   primaryContact: new FormControl({}),
    //   secondaryContact: new FormControl({}),
    //   email: new FormControl('', [Validators.email]),
    //   buildingNo: new FormControl(''),
    //   buildingName: new FormControl(''),
    //   addressLine1: new FormControl(''),
    //   addressLine2: new FormControl(''),
    //   position: new FormControl(''),
    //   city: new FormControl('Dubai', [Validators.required]),
    //   country: new FormControl('AE', [Validators.required]),
    // });

    this.pageForm = new FormGroup({
      groupType: new FormControl(GroupType.church.valueOf()),
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(50),
      ]),
      description: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(1000),
      ]),
      academicYear: new FormControl(moment().year(), [Validators.required]),
      primaryContact: new FormControl({}),
      secondaryContact: new FormControl({}),
      email: new FormControl('', [Validators.email]),
      buildingNo: new FormControl(''),
      buildingName: new FormControl(''),
      addressLine1: new FormControl(''),
      addressLine2: new FormControl(''),
      position: new FormControl(''),
      city: new FormControl('', [Validators.required]),
      country: new FormControl('', [Validators.required]),
      currency: new FormControl('usd', [Validators.required]),
      fiscalYearStart: new FormControl(''),
      fiscalYearEnd: new FormControl(''),
      isAiEnabled: new FormControl(true),
      isChatAllowed: new FormControl(true),
      bibleType: new FormControl('protestant', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
        if (user) {
          this.pageForm.patchValue({
            email: user.email,
            primaryContact: user.contact,
            city: user.address?.city,
            country: user.address?.country,
          });

          const userCountry = user.address?.country;
          if (userCountry) {
            const code = userCountry.toUpperCase();
            let defaultCurrency = 'usd';
            if (code === 'GB') {
              defaultCurrency = 'gbp';
            } else if (
              [
                'AT', 'BE', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT',
                'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES'
              ].includes(code)
            ) {
              defaultCurrency = 'eur';
            }
            this.pageForm.patchValue({ currency: defaultCurrency });
          }
        }
      });

    this.pageForm.get('country')?.valueChanges
      .pipe(takeUntil(this.exit$))
      .subscribe((countryCode) => {
        if (countryCode) {
          const code = countryCode.toUpperCase();
          let defaultCurrency = 'usd';
          if (code === 'GB') {
            defaultCurrency = 'gbp';
          } else if (
            [
              'AT', 'BE', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT',
              'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES'
            ].includes(code)
          ) {
            defaultCurrency = 'eur';
          }
          this.pageForm.patchValue({ currency: defaultCurrency });
        }
      });

    this.store
      .select(groupSelector.inProgress)
      .pipe(takeUntil(this.exit$))
      .subscribe((inProgress) => {
        this.working = inProgress;
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  setPosition(position: GeoPoint | null) {
    this.pageForm.patchValue({ position: position });
  }

  create() {
    if (this.pageForm.invalid) return;

    let data: any = {
      groupType: this.pageForm.value.groupType,
      academicYear: Number(this.pageForm.value.academicYear),
      name: this.pageForm.value.name,
      description: this.pageForm.value.description,
      email: this.pageForm.value.email,
      address: {
        buildingNo: this.pageForm.value.buildingNo,
        buildingName: this.pageForm.value.buildingName,
        addressLine1: this.pageForm.value.addressLine1,
        addressLine2: this.pageForm.value.addressLine2,
        city: this.pageForm.value.city,
        country: this.pageForm.value.country,
      },
      currency: this.pageForm.value.currency || 'usd',
      fiscalYearStart: this.pageForm.value.fiscalYearStart ? new Date(this.pageForm.value.fiscalYearStart) : null,
      fiscalYearEnd: this.pageForm.value.fiscalYearEnd ? new Date(this.pageForm.value.fiscalYearEnd) : null,
      isAiEnabled: this.pageForm.value.isAiEnabled !== false,
      isChatAllowed: this.pageForm.value.isChatAllowed !== false,
      bibleType: this.pageForm.value.bibleType || 'protestant',
    };

    if (this.pageForm.value?.primaryContact?.contact) {
      data.primaryContact = this.pageForm.value.primaryContact;
    }

    if (this.pageForm.value?.secondaryContact?.contact) {
      data.secondaryContact = this.pageForm.value.secondaryContact;
    }

    //TODO: Firebase function does not accept GeoPoint. To send GeoPoint, fix mobile and webapp to send GeoPoint
    if (this.pageForm.get('position')?.value) {
      data.location = {
        id: this.uuid,
        position: {
          lat: this.pageForm.get('position')?.value.latitude,
          lng: this.pageForm.get('position')?.value.longitude,
        },
        name: data.name,
      };
    }

    // console.log(`Create group form: ${JSON.stringify(data, null, 2)}`);

    this.store.dispatch(groupActions.createGroup({ group: data }));
  }
}
